'use client';
import React, {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import PropTypes from 'prop-types';

import Dropdown from '../../../../components/Dropdown/Dropdown';
import Map from '../../../../components/Map/Map';
import MapDateRangeFilter from '../../../../components/MapDateRangeFilter';
import MapFilterPanel from '../../../../components/MapFilterPanel';
import MapLayerList from '../../../../components/MapLayerList';
import { t } from '../../../../i18n';
import {
    getCountryBounds,
    getPointsBounds,
    padCountryBounds,
} from '../../../../utils/map/bounds';
import HybridActivityLayerSwatch from './HybridActivityLayerSwatch';
import {
    renderHybridActivityPoints,
    whenStyleReady,
} from './hybridActivityLayer';
import {
    selectVisiblePoints,
    withVisibility,
} from './hybridActivityLayerConfig';
import { attachPointCards } from './hybridActivityPointCards';

import s from './HybridActivityMap.module.scss';

const POINTS_FRAME_PADDING = 60;

// What the panel takes when it cannot be measured: .Filters' width plus its
// right inset, as the stylesheet has them. A fallback only — the framing
// measures the rendered panel, so a stylesheet tweak or the mobile max-width
// clamp is picked up without touching this.
const FILTER_PANEL_FALLBACK_CLEARANCE = 280 + 16;

// How much of the map's right side the filter panel covers: from the panel's
// left edge to the map's right edge, as rendered.
const panelClearance = (map, panel) => {
    if (panel) {
        const clearance =
            map.getContainer().getBoundingClientRect().right -
            panel.getBoundingClientRect().left;

        if (clearance > 0) {
            return clearance;
        }
    }

    return FILTER_PANEL_FALLBACK_CLEARANCE;
};

// On a narrow map the panel's clearance would squeeze the points into a
// sliver — or past what fitBounds accepts at all — so it is only kept clear
// while the points still get at least as much width as the panel takes;
// below that the panel covering a point beats the frame collapsing.
const pointsFramePadding = (map, panel) => {
    const mapWidth = map.getContainer().clientWidth;
    const clearance = panelClearance(map, panel);
    const clearedRight = POINTS_FRAME_PADDING + clearance;

    if (mapWidth - clearedRight - POINTS_FRAME_PADDING < clearance) {
        return POINTS_FRAME_PADDING;
    }

    return {
        top: POINTS_FRAME_PADDING,
        bottom: POINTS_FRAME_PADDING,
        left: POINTS_FRAME_PADDING,
        right: clearedRight,
    };
};

// The one layer no editor authors: the client's own sites in this country.
// Authored layers go by the dataset value they claim; this is the id the
// holdings row goes by instead.
const HOLDINGS_LAYER_ID = 'holdings';

const HybridActivityMap = ({
    countryCode,
    config,
    points,
    pointsInWindow,
    unclaimedValues = [],
    hasFailed,
    dateExtent,
    lastUpdated,
    range,
    appliedRange,
    onSelectRange,
    isPreview,
    htmlPath,
}) => {
    const [map, setMap] = useState(null);
    // The rendered filter panel, measured when framing the points so the
    // clearance tracks what the stylesheet actually gave it.
    const filterPanelRef = useRef(null);
    // Held as what a reader switched off rather than what is on, so every layer
    // starts visible and one added in the CMS later arrives visible too.
    const [hiddenLayerIds, setHiddenLayerIds] = useState(() => new Set());
    // Holdings run the other way: off until asked for, since nobody has asked
    // for them as a default and switching them on is one click.
    const [isHoldingsLayerOn, setIsHoldingsLayerOn] = useState(false);

    // The one description of a layer: what the picker ticks and what the map
    // draws.
    const layers = useMemo(
        () => withVisibility(config, hiddenLayerIds),
        [config, hiddenLayerIds]
    );

    // The authored layers plus the one that is not: holdings, always on offer,
    // described here rather than in the CMS because there is exactly one of it
    // and it means the same on every country. Listed last, so the authored
    // ordering stays the editor's. Never dated — a site does not stop existing
    // because a reader scrubbed to last month — which holds by construction:
    // the date window only ever reads the incident points.
    const listedLayers = useMemo(
        () => [
            ...layers,
            {
                id: HOLDINGS_LAYER_ID,
                name: t('watchtowerCountryPage.hybridActivityHoldingsLayer'),
                isHoldings: true,
                isVisible: isHoldingsLayerOn,
            },
        ],
        [layers, isHoldingsLayerOn]
    );

    const toggleLayer = useCallback((id, isVisible) => {
        if (id === HOLDINGS_LAYER_ID) {
            setIsHoldingsLayerOn(isVisible);
            return;
        }

        setHiddenLayerIds((hidden) => {
            const next = new Set(hidden);

            if (isVisible) {
                next.delete(id);
            } else {
                next.add(id);
            }

            return next;
        });
    }, []);

    // A dataset with no dates — or with every event on the same day, where there
    // is no window to narrow — has no range to offer. The layers keep the
    // control either way, since the holdings layer is always among them.
    const hasDateRange = Boolean(dateExtent) && dateExtent.max > dateExtent.min;

    // Counted as filters rather than as choices: the layers a reader has moved
    // off their defaults — authored ones switched off, holdings switched on —
    // are one thing changing what the map shows, and a window is another. What
    // the count is for is telling a reader that the map is not showing its
    // untouched self, which is when there is something to clear. The Layers
    // section's own checkmark is not this: it marks the section as having
    // something ticked in it, which is what a checkmark beside a list of
    // checkboxes can only mean.
    const changedLayerCount =
        layers.filter((layer) => !layer.isVisible).length +
        (isHoldingsLayerOn ? 1 : 0);
    const activeFilterCount =
        (changedLayerCount > 0 ? 1 : 0) + (appliedRange ? 1 : 0);

    // Back to the defaults, which for holdings means off again.
    const clearFilters = useCallback(() => {
        setHiddenLayerIds(new Set());
        setIsHoldingsLayerOn(false);
        onSelectRange(null);
    }, [onSelectRange]);

    // Layer visibility and the date window compose: a point shows when its
    // layer is on and it falls inside the window the tab has already applied.
    const visiblePoints = useMemo(
        () => selectVisiblePoints(pointsInWindow, layers, config.column),
        [pointsInWindow, layers, config.column]
    );

    useEffect(() => {
        if (!unclaimedValues.length) {
            return;
        }

        // Said out loud even outside preview: a value appearing in the data that
        // no layer claims is a configuration gap, not a rendering detail.
        console.warn(
            `Hybrid activity: no layer claims ${unclaimedValues
                .map((value) => `"${value}"`)
                .join(', ')}. Those points are not drawn.`
        );
    }, [unclaimedValues]);

    // Renders an empty collection too, so points that go away are cleared
    // rather than left on the map.
    useEffect(() => {
        if (!map || !visiblePoints) {
            return undefined;
        }

        return whenStyleReady(map, () => {
            renderHybridActivityPoints(map, visiblePoints, config);
        });
    }, [map, visiblePoints, config]);

    // Mapbox holds a layer-scoped listener until the layer exists, so this needs
    // no wait. Re-attached whenever what is drawn changes, because the teardown
    // dismisses any open card: a card describing a point that is no longer drawn
    // — its layer switched off, scrubbed out of the date range, or edited away in
    // preview — has nothing left to describe.
    useEffect(() => {
        if (!map) {
            return undefined;
        }

        return attachPointCards(map, config);
    }, [map, config, layers, appliedRange]);

    // Frame the country while the dataset is still loading, then the incidents
    // once they land — which for an attributed country need not sit inside it.
    // Framed on every configured point, not the visible ones: a reader hiding a
    // layer to look closer at another does not want the map moving under them.
    useEffect(() => {
        if (!map) {
            return;
        }

        const pointsBounds = getPointsBounds(points);

        if (pointsBounds) {
            map.fitBounds(pointsBounds, {
                padding: pointsFramePadding(map, filterPanelRef.current),
                duration: 0,
            });
            return;
        }

        const countryBounds = padCountryBounds(getCountryBounds(countryCode));

        if (countryBounds) {
            map.fitBounds(countryBounds, { padding: 0, duration: 0 });
        }
    }, [map, points, countryCode]);

    const hasNothingToPlot = points !== null && points.features.length === 0;
    let message = null;

    if (hasFailed) {
        message = t('watchtowerCountryPage.hybridActivityLoadError');
    } else if (hasNothingToPlot) {
        message = t('watchtowerCountryPage.hybridActivityEmptyState');
    }

    // A reader gets the reader-facing empty state either way; the diagnosis of
    // *why* it is empty belongs to whoever can act on it.
    let editorNotice = null;

    if (isPreview && !config.layers.length) {
        editorNotice = t('watchtowerCountryPage.hybridActivityNoLayers');
    } else if (isPreview && unclaimedValues.length) {
        editorNotice = t(
            'watchtowerCountryPage.hybridActivityUnclaimedLayers',
            {
                values: unclaimedValues
                    .map(
                        (value) =>
                            value ||
                            t(
                                'watchtowerCountryPage.hybridActivityBlankLayerValue'
                            )
                    )
                    .join(', '),
            }
        );
    }

    return (
        <div className={s.Root}>
            {/* Which layers are drawn and which window of time, in the
                one place a reader goes to narrow what they are looking
                at. Always offered: the holdings layer is in the list
                whatever an editor configured. Over the map's top-right
                corner on a wide viewport, rather than in Map's control row,
                which sits top-left; on a narrow one it takes the full width
                above the map, where it covers no ground at all. The sections
                start expanded — the panel is the map's table of contents —
                and each collapses on its own header, the panel as a whole on
                its button. */}
            <div className={s.Filters} ref={filterPanelRef}>
                <MapFilterPanel
                    title={t('watchtowerCountryPage.hybridActivityFilters')}
                    activeCount={activeFilterCount}
                    // The holdings filter's own words, so the two panels
                    // read as the same control.
                    clearLabel={t('mapFilter.clear')}
                    onClear={clearFilters}>
                    <Dropdown
                        isOpen
                        modifiers={['BorderBottom']}
                        title={t('watchtowerCountryPage.hybridActivityLayers')}
                        showCheckmark={listedLayers.some(
                            (layer) => layer.isVisible
                        )}>
                        <MapLayerList
                            title={t(
                                'watchtowerCountryPage.hybridActivityLayers'
                            )}
                            layers={listedLayers}
                            onToggle={toggleLayer}
                            renderSwatch={(layer) => (
                                <HybridActivityLayerSwatch layer={layer} />
                            )}
                        />
                    </Dropdown>

                    {hasDateRange && (
                        <Dropdown
                            isOpen
                            modifiers={['BorderBottom']}
                            title={t(
                                'watchtowerCountryPage.hybridActivityDates'
                            )}
                            showCheckmark={Boolean(appliedRange)}>
                            <div className={s.FilterSectionContent}>
                                <MapDateRangeFilter
                                    min={dateExtent.min}
                                    max={dateExtent.max}
                                    value={range}
                                    isNarrowed={Boolean(appliedRange)}
                                    onChange={onSelectRange}
                                    fromLabel={t(
                                        'watchtowerCountryPage.hybridActivityDateFrom'
                                    )}
                                    toLabel={t(
                                        'watchtowerCountryPage.hybridActivityDateTo'
                                    )}
                                    dateLabel={t(
                                        'watchtowerCountryPage.hybridActivityDate'
                                    )}
                                    pickerLabel={t(
                                        'watchtowerCountryPage.hybridActivityDatePicker'
                                    )}
                                    dateModeLabel={t(
                                        'watchtowerCountryPage.hybridActivitySwitchToDate'
                                    )}
                                    rangeModeLabel={t(
                                        'watchtowerCountryPage.hybridActivitySwitchToRange'
                                    )}
                                />
                            </div>
                        </Dropdown>
                    )}
                </MapFilterPanel>
            </div>

            <div className={s.MapArea}>
                <Map
                    isSingleCountry
                    // Fetched the first time a reader switches holdings on —
                    // the ask latches in Map, so an untouched map still pays
                    // nothing for them. No singleCountryCode scopes the fetch:
                    // the backend then covers every watchtower country on the
                    // client's dashboard, because the incidents scatter across
                    // borders and a site just over one still answers "is this
                    // happening near us?".
                    shouldFetchHoldings={isHoldingsLayerOn}
                    htmlPath={htmlPath}
                    // Visibility apart from fetching: switching off keeps what
                    // arrived, so switching back on redraws without a request.
                    isHoldingsLayerShown={isHoldingsLayerOn}
                    // A clicked holdings marker raises the same company popup
                    // the watchtower world map shows.
                    isPointPopupShown
                    // Holdings are one layer beside the incident layers here,
                    // not the whole map, so no curtain while they load.
                    isLoadingOverlayShown={false}
                    // The tab's whole content, so no room is kept for a list
                    // beside it: the map takes the height it is given at every
                    // width, rather than Map's tablet cap.
                    isFullHeight
                    // The freshness strip below closes the card, so the map's
                    // own bottom corners give up their rounding to it.
                    isBottomFlush={Boolean(lastUpdated)}
                    shouldFrameSingleCountry={false}
                    onMapReady={setMap}
                />

                {editorNotice && (
                    <div className={s.EditorNotice}>{editorNotice}</div>
                )}

                {message && (
                    <div className={s.EmptyState}>
                        <p className={s.EmptyStateMessage}>{message}</p>
                    </div>
                )}
            </div>

            {/* When the dataset was uploaded, or its latest event date while
                Fenix carries no upload stamp: an upload with no new events
                would otherwise read as stale. The basemap's own attribution
                stays where Mapbox puts it, in the map's bottom-right corner. */}
            {lastUpdated && (
                <p className={s.Footer}>
                    {t('watchtowerCountryPage.hybridActivityLastUpdated', {
                        date: lastUpdated,
                    })}
                </p>
            )}
        </div>
    );
};

const featureCollection = PropTypes.shape({
    type: PropTypes.string,
    features: PropTypes.array,
});

const dateRange = PropTypes.shape({
    from: PropTypes.string,
    to: PropTypes.string,
});

// The tab owns the dataset fetch and the date window, so the map and the
// incident log beside it read the same points and the same applied range. The
// map's own state is what only drawing cares about: which layers are shown.
HybridActivityMap.propTypes = {
    countryCode: PropTypes.string,
    // The authored layer configuration as toLayerConfig normalised it.
    config: PropTypes.shape({
        column: PropTypes.string,
        layers: PropTypes.arrayOf(
            PropTypes.shape({
                name: PropTypes.string,
                matchValue: PropTypes.string,
                icon: PropTypes.string,
                colour: PropTypes.string,
                isDated: PropTypes.bool,
            })
        ),
    }).isRequired,
    // Every configured point — what the framing and the empty state answer to —
    // and the subset inside the applied window, which is what gets drawn.
    points: featureCollection,
    pointsInWindow: featureCollection,
    unclaimedValues: PropTypes.arrayOf(PropTypes.string),
    hasFailed: PropTypes.bool,
    dateExtent: PropTypes.shape({
        min: PropTypes.string,
        max: PropTypes.string,
    }),
    lastUpdated: PropTypes.string,
    range: dateRange,
    appliedRange: dateRange,
    onSelectRange: PropTypes.func.isRequired,
    isPreview: PropTypes.bool,
    // The page's own path, for the holdings fetch to resolve the dashboard by
    // when the browser's path is the preview route rather than the page's.
    htmlPath: PropTypes.string,
};

export default HybridActivityMap;
